import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const converted = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
            converted[camelKey] = typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])
                ? toCamelCase(obj[key])
                : obj[key];
        }
    }
    return converted;
};

export const annotationService = {
  async getByProductId(productId) {
    const { data, error } = await supabase
      .from('model_annotations')
      .select(`
        *,
        user:users!model_annotations_user_id_fkey(id, email, first_name, last_name),
        component:components(id, name, custom_name)
      `)
      .eq('product_id', productId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async create(annotationData) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbData = {
      product_id: annotationData.productId,
      component_id: annotationData.componentId || null,
      component_name: annotationData.componentName || null,
      admin_comment: annotationData.componentName || null, // Leverage existing column as fallback!
      text: annotationData.text,
      user_id: user.id,
      position_x: annotationData.position?.x,
      position_y: annotationData.position?.y,
      position_z: annotationData.position?.z
    };

    const { data, error } = await supabase
      .from('model_annotations')
      .insert(dbData)
      .select(`
        *,
        user:users!model_annotations_user_id_fkey(id, email, first_name, last_name),
        component:components(id, name, custom_name)
      `)
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async getAll() {
    const { data, error } = await supabase
      .from('model_annotations')
      .select(`
        *,
        user:users!model_annotations_user_id_fkey(id, email, first_name, last_name),
        component:components(id, name, custom_name),
        product:products(id, name)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async updateAdminComment(id, adminComment) {
    const { data, error } = await supabase
      .from('model_annotations')
      .update({ admin_comment: adminComment })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async delete(id) {
    const { error } = await supabase
      .from('model_annotations')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  async updateUserAnnotation(id, text) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('model_annotations')
      .update({ text })
      .eq('id', id)
      .eq('user_id', user.id) // Securely restrict update to the author only!
      .select(`
        *,
        user:users!model_annotations_user_id_fkey(id, email, first_name, last_name),
        component:components(id, name, custom_name)
      `)
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async vote(id, type) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // To prevent race conditions, fetch current counts and voter arrays
    const { data: current, error: fetchErr } = await supabase
      .from('model_annotations')
      .select('thumbs_up, thumbs_down, upvoted_by, downvoted_by')
      .eq('id', id)
      .single();
    if (fetchErr) throw fetchErr;

    let upBy = current.upvoted_by || [];
    let downBy = current.downvoted_by || [];
    let upCount = current.thumbs_up || 0;
    let downCount = current.thumbs_down || 0;
    const userId = user.id;

    if (type === 'up') {
      if (upBy.includes(userId)) {
        // Retract upvote
        upBy = upBy.filter(uid => uid !== userId);
        upCount = Math.max(0, upCount - 1);
      } else {
        // Add upvote, and retract downvote if present
        upBy = [...upBy, userId];
        upCount = upCount + 1;
        if (downBy.includes(userId)) {
          downBy = downBy.filter(uid => uid !== userId);
          downCount = Math.max(0, downCount - 1);
        }
      }
    } else {
      if (downBy.includes(userId)) {
        // Retract downvote
        downBy = downBy.filter(uid => uid !== userId);
        downCount = Math.max(0, downCount - 1);
      } else {
        // Add downvote, and retract upvote if present
        downBy = [...downBy, userId];
        downCount = downCount + 1;
        if (upBy.includes(userId)) {
          upBy = upBy.filter(uid => uid !== userId);
          upCount = Math.max(0, upCount - 1);
        }
      }
    }

    const updates = {
      thumbs_up: upCount,
      thumbs_down: downCount,
      upvoted_by: upBy,
      downvoted_by: downBy
    };

    const { data, error } = await supabase
      .from('model_annotations')
      .update(updates)
      .eq('id', id)
      .select(`
        *,
        user:users!model_annotations_user_id_fkey(id, email, first_name, last_name),
        component:components(id, name, custom_name)
      `)
      .single();

    if (error) throw error;
    return toCamelCase(data);
  }
};
